import React, { useMemo } from 'react';
import merge from 'lodash.merge';

import { DynamicIcon } from '@/components/ui/dynamicIcon/dynamicIcon';
import { getByPath } from 'utils/widgets';
import { WidgetData } from '../../widget.type';
import { IconView } from './icon.view.type';

type Props = {
  view: IconView;
  data: WidgetData;
  update: {
    patch: WidgetData;
  };
};

const IconViewImpl: React.FC<Props> = ({ view, data, update }) => {
  const { path, iconPath, labelPath, icon } = view;
  const { patch } = update || {};

  // check if the data to display has been patched
  const dataWithPatchApplied = useMemo(
    () => merge({}, data, patch),
    [data, patch]
  );

  // if a 'path' has been specified we assume this is to check whether the icon should be displayed or not
  if (path && !getByPath(dataWithPatchApplied, path)) {
    return null;
  }
  const iconStr =
    getByPath(dataWithPatchApplied, iconPath) || icon || 'Document';
  const iconComponent = <DynamicIcon icon={iconStr} size={24} />;
  const label = getByPath(dataWithPatchApplied, labelPath);

  return (
    <div className="flex h-full flex-row items-center justify-start overflow-hidden">
      <span className="flex-none">{iconComponent}</span>
      {label && <span className="ml-[5px] truncate">{label}</span>}
    </div>
  );
};

// sort defaults to text on iconPath or labelPath, or binary on path
const getSortConfig = (view: IconView) => {
  const { path, labelPath, iconPath } = view;
  if (!labelPath && !iconPath) return { type: 'binary', path };
  return {
    type: 'text',
    path: labelPath || iconPath
  };
};

// filtering defaults to text search on iconPath
const getFilterConfig = (view: IconView) => ({
  type: 'text',
  path: view.labelPath || view.iconPath || view.path
});

// width defaults to 'medium' if a label is shown, 'small' otherwise
const getDefaultWidth = (view: IconView) =>
  !view.labelPath ? 'small' : 'medium';

// stringifies data at path
const getStringifyFunction = (view: IconView) => {
  const { path, labelPath } = view;

  // output either labelPath if specified
  if (labelPath !== undefined)
    return (data: WidgetData) => getByPath(data, labelPath);

  // or just the boolean value associated to path (that triggered the icon rendering)
  if (path !== undefined)
    return (data: WidgetData) => (!!getByPath(data, path)).toString();

  return () => '';
};

export const iconViewProvider = {
  component: IconViewImpl,
  getSortConfig,
  getFilterConfig,
  getDefaultWidth,
  getStringifyFunction
};
